// Same rules as Backend_Subjective_Checker/utils/ocrNoise.js.
// A saved transcript that is model reasoning, or a copy of the OCR
// instructions. "blank page" and a lone [unclear] are real results.

const PAGE_HEADER_RE = /^[ \t]*(===\s*Page\s+\d+(?:\s*===)?|---\s*[^\n]*?\.(?:jpe?g|png|webp|gif|bmp|tiff?)\s*---)[ \t]*$/gim;

export function splitOcrPages(text) {
  const source = String(text || '');
  const markers = [];
  const re = new RegExp(PAGE_HEADER_RE.source, 'gim');
  let match;
  while ((match = re.exec(source))) {
    markers.push({
      header: match[1].trim(),
      start: match.index,
      end: match.index + match[0].length
    });
  }
  if (!markers.length) return [{ header: '', body: source }];
  const pages = [];
  const pre = source.slice(0, markers[0].start).trim();
  if (pre) pages.push({ header: '', body: pre });
  for (let i = 0; i < markers.length; i++) {
    const bodyEnd = i + 1 < markers.length ? markers[i + 1].start : source.length;
    pages.push({
      header: markers[i].header,
      body: source.slice(markers[i].end, bodyEnd)
    });
  }
  return pages;
}

const THINKING_PHRASES = [
  [/\bLOOK AT LINE\b/i, 'LOOK AT LINE'],
  [/WHERE DOES(?: THE)? URDU START/i, 'WHERE DOES URDU START'],
  [/URDU STARTS ON THE RIGHT/i, 'URDU STARTS ON THE RIGHT'],
  [/\bcircle with (?:a |something )/i, 'circle with'],
  [/\bIs that ["'“”]?Q/i, 'Is that Q'],
  [/\bLet(?:'|’)s look at\b/i, "Let's look at"],
  [/^[ \t]*WAIT![ \t]*$/im, 'WAIT!'],
  [/\bOn the far right\b/i, 'On the far right']
];

function phraseHits(raw, table) {
  const hits = [];
  for (const [re, label] of table) {
    if (re.test(raw) && !hits.includes(label)) hits.push(label);
  }
  return hits;
}

function ocrNoiseKind(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  if (/^blank page[.!]?$/i.test(raw)) return null;
  if (/^\[unclear\]$/i.test(raw)) return null;

  if (/STEP\s*1\s*[-—]\s*RAW OCR|NON-NEGOTIABLE OCR RULES|MANDATORY SELF-CHECK|FINAL REMINDERS|PRESERVE LINE BREAKS/i.test(raw)) {
    return 'prompt';
  }
  if (
    /\bLOOK AT LINE\b/i.test(raw)
    || /WHERE DOES(?: THE)? URDU START/i.test(raw)
    || /URDU STARTS ON THE RIGHT/i.test(raw)
    || /\bcircle with (?:a |something )/i.test(raw)
    || /\bIs that ["'“”]?Q/i.test(raw)
    || /\bLet(?:'|’)s look at\b/i.test(raw)
    || /^[ \t]*WAIT![ \t]*$/im.test(raw)
    || (/\bOn the far right\b/i.test(raw) && /\b(circle|LOOK AT|WAIT!)\b/i.test(raw))
  ) {
    return 'thinking';
  }
  return null;
}

function noiseWhy(kind) {
  if (kind === 'thinking') return 'thinking';
  if (kind === 'prompt') return 'instructions';
  return 'chatter';
}

export function parseChatterOk(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function chatterBodyKey(body) {
  const text = String(body || '').trim();
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16);
}

function pageRegions(text) {
  const source = String(text || '');
  const pages = splitOcrPages(source);
  const regions = [];
  let searchFrom = 0;
  for (const page of pages) {
    const header = String(page.header || '').trim();
    const body = String(page.body || '');
    let bodyStart = searchFrom;
    if (body) {
      const atBody = source.indexOf(body, searchFrom);
      if (atBody >= 0) bodyStart = atBody;
    }
    const pageMatch = header.match(/Page\s+(\d+)/i);
    const pageNo = pageMatch ? Number(pageMatch[1]) : (pages.length === 1 ? 1 : 0);
    regions.push({ page: pageNo, header, body, bodyStart });
    searchFrom = bodyStart + body.length;
  }
  return regions;
}

function matchingLineRanges(source, bodyStart, body, phrases) {
  const ranges = [];
  const lines = String(body || '').split('\n');
  let cursor = bodyStart;
  const tests = phrases.map((label) => {
    const row = THINKING_PHRASES.find((item) => item[1] === label);
    return row ? row[0] : null;
  }).filter(Boolean);
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed && tests.some((re) => re.test(trimmed))) {
      const at = line.indexOf(trimmed);
      const start = cursor + (at >= 0 ? at : 0);
      ranges.push({ start, end: start + trimmed.length, text: trimmed });
    }
    cursor += line.length + 1;
  }
  return ranges;
}

function findingPhrases(kind, body) {
  if (kind === 'thinking') return phraseHits(body, THINKING_PHRASES);
  if (kind === 'prompt') return ['OCR instructions'];
  return [];
}

export function chatterFindings(text) {
  const source = String(text || '');
  const out = [];
  for (const region of pageRegions(source)) {
    const kind = ocrNoiseKind(region.body);
    if (!kind || kind === 'prompt') continue;
    const phrases = findingPhrases(kind, region.body);
    out.push({
      page: region.page,
      kind,
      phrases,
      bodyKey: chatterBodyKey(region.body),
      lines: matchingLineRanges(source, region.bodyStart, region.body, phrases)
    });
  }
  return out;
}

export function activeChatterFindings(text, accepted) {
  const ok = parseChatterOk(accepted);
  return chatterFindings(text).filter((finding) => !(finding.page && ok[String(finding.page)] === finding.bodyKey));
}

export function isSuspectedNote(note) {
  return /^(?:p\d+\s+)?(?:chatter|thinking|not handwriting)$/i.test(String(note || '').trim());
}

export function suspectedPagesFromNotes(notes) {
  const pages = [];
  for (const note of notes || []) {
    const match = String(note || '').trim().match(/^(?:p(\d+)\s+)?(chatter|thinking|not handwriting)$/i);
    if (!match || !match[1]) continue;
    const page = Number(match[1]);
    const kind = /thinking/i.test(match[2]) ? 'thinking' : 'chatter';
    if (!pages.some((item) => item.page === page)) pages.push({ page, kind, label: `p${page}` });
  }
  return pages;
}

export function noiseNotes(text, accepted) {
  const ok = parseChatterOk(accepted);
  const notes = [];
  for (const region of pageRegions(text)) {
    const kind = ocrNoiseKind(region.body);
    if (!kind) continue;
    if (kind !== 'prompt' && region.page && ok[String(region.page)] === chatterBodyKey(region.body)) continue;
    const where = region.page ? `p${region.page}` : '';
    const why = noiseWhy(kind);
    const note = where ? `${where} ${why}` : why;
    if (!notes.includes(note)) notes.push(note);
  }
  return notes;
}
